import {customElement, property} from "lit/decorators.js";
import {Dictionary} from "../../../elements/dictionary";
import {html, type TemplateResult} from "lit";
import Styles from "./navigationSheet.lit.scss";
import type {MetaLink} from "../common/navLinkTypes";
import {type NavigationItem, type NavigationLevel1Item, NavModel} from "../common/navModel";
import {navigationUpEvent} from "./mobileHeaderEvents";
import {EopNavigationItemGroup} from "../common/navigationItemGroup";
import {resolve} from "../../../../container";
import {ClickTracking} from "../../../../tracking/clickTracking";
import {TrackingEventLocation, TrackingEventName, TrackingEventType} from "../../../../tracking/types";
import type {EopNavigationElement} from "./navigationElement";
import type {EopMobileLanguageSwitch} from "./mobileLanguageSwitch";
import type {EopColorSchemeToggle} from "../common/colorSchemeToggle";
import type {Nullable} from "../../../../bootstrap/common/types";

@customElement("eop-navigation-sheet")
export class EopNavigationSheet extends EopNavigationItemGroup {

    public static readonly styles = Styles;

    @property()
    public navItem: Nullable<NavigationItem>;
    @property()
    public sectorLinks: NavigationLevel1Item[] = [];
    @property()
    public metaLinks: MetaLink[] = [];
    @property({attribute: "with-language-switch", type: Boolean})
    public withLanguageSwitch: boolean = false;
    @property()
    public level: number;
    @property({attribute: "with-dark-mode-toggle", type: Boolean})
    private withDarkModeToggle: boolean = false;
    @property({reflect: true, type: Boolean})
    public active: boolean = false;

    public constructor(
        private tracking: ClickTracking = resolve(ClickTracking),
        private navModel: NavModel = resolve(NavModel)
    ) {
        super();
    }

    private dictionary: Dictionary;

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
    }

    public render(): TemplateResult {
        if (!this.navItem && this.metaLinks.isEmpty() && this.sectorLinks.isEmpty()) {
            return html`
                <div class="mobile-sub-menu-container single-block" data-eventelement="metanavigation">
                    ${this.renderDarkModeToggle()}
                    ${this.renderLanguageSwitch()}
                </div>
            `;
        } else {
            return html`
                <div class="mobile-sub-menu-container multi-block">
                    <div class="mobile-menu-top">
                        ${this.renderContextNavigation()}
                        ${this.renderMainNavigation()}
                        ${this.renderMetaAndSectorNavigation()}
                    </div>
                    <div class="mobile-menu-bottom" data-eventelement="metanavigation">
                        ${this.renderLanguageSwitch()}
                        ${this.renderDarkModeToggle()}
                    </div>
                </div>
            `;
        }
    }

    public lastFocusableElement(): Nullable<HTMLElement> {
        const colorToggle = this.renderRoot.querySelector<EopColorSchemeToggle>("eop-color-scheme-toggle");
        if (colorToggle) {
            return colorToggle.focusableElement();
        }

        const langSwitch = this.renderRoot.querySelector<EopMobileLanguageSwitch>("eop-mobile-language-switch");
        const langLinks = langSwitch ? langSwitch.focusableElements() : [];
        if (langLinks.isNotEmpty()) {
            return langLinks.last()!;
        }

        const metaLinks = Array.from(this.renderRoot.querySelectorAll<HTMLAnchorElement>(".sector-link, .meta-link"));
        if (metaLinks.isNotEmpty()) {
            return metaLinks.last()!;
        }

        const navLinks = Array.from(this.renderRoot.querySelectorAll<EopNavigationElement>("eop-navigation-element"))
            .map(element => element.focusableElement())
            .filter(element => element !== null);
        if (navLinks.isNotEmpty()) {
            return navLinks.last()!;
        }

        const parentLink = this.renderRoot.querySelector<HTMLAnchorElement>(".parent-link");
        if (parentLink) {
            return parentLink;
        }

        const backNavigation = this.renderRoot.querySelector<HTMLButtonElement>(".back-navigation");
        if (backNavigation) {
            return backNavigation;
        }

        return null;
    }

    private renderMainNavigation(): Nullable<TemplateResult> {
        if (!this.navItem?.hasSubItems()) {
            return null;
        }

        return html`
            <div class="main-navigation" data-eventelement="mainnavigation">
                ${(this.renderItems())}
            </div>
        `;
    }

    private renderItems(): TemplateResult[] {
        return this.navItem!.getSubItems().map(item => html`
            <eop-navigation-element .navItem=${item} .level=${this.level}></eop-navigation-element>`
        );
    }

    private renderMetaAndSectorNavigation(): Nullable<TemplateResult> {
        if (this.sectorLinks.length <= 1 && this.metaLinks.isEmpty()) {
            return null;
        }

        return html`
            <div class="meta-navigation" data-eventelement="metanavigation">
                ${(this.renderSectorLinks())}
                ${(this.renderMetaLinks())}
            </div>
        `;
    }

    private renderSectorLinks(): Nullable<TemplateResult> {
        if (this.sectorLinks.length <= 1) {
            return null;
        }
        const sectorLinksRendered = this.sectorLinks.map(sectorLink => this.renderSectorLink(sectorLink));
        return html`
            <div class="meta-navigation-block">
                <div class="meta-navigation-block-headline">${this.navModel.getSectorLinksHeadline()}</div>
                ${sectorLinksRendered}
            </div>
        `;
    }

    private renderSectorLink(sectorLink: NavigationLevel1Item): TemplateResult {
        const classes = `sector-link link${sectorLink.active ? " highlighted" : ""}`;
        return html`
            <a href=${sectorLink.href} target=${sectorLink.target} title=${sectorLink.label}
               class=${classes}>${sectorLink.label}</a>`;
    }

    private renderMetaLinks(): Nullable<TemplateResult> {
        if (this.metaLinks.isEmpty()) {
            return null;
        }
        const metaLinksRendered = this.metaLinks.map(metaLink => this.renderMetaLink(metaLink));
        return html`
            <div class="meta-navigation-block">
                <div class="meta-navigation-block-headline">${this.navModel.getMetaLinksHeadline()}</div>
                ${metaLinksRendered}
            </div>
        `;
    }

    private renderMetaLink(metaLink: MetaLink): TemplateResult {
        return html`
            <a href=${metaLink.href} target=${metaLink.target} title=${metaLink.label}
               class="meta-link link">${metaLink.label}</a>`;
    }

    private renderDarkModeToggle(): Nullable<TemplateResult> {
        if (!this.withDarkModeToggle) {
            return null;
        }

        return html`
            <eop-color-scheme-toggle></eop-color-scheme-toggle>`;
    }


    private renderLanguageSwitch(): Nullable<TemplateResult> {
        if (!this.withLanguageSwitch) {
            return null;
        }

        return html`
            <eop-mobile-language-switch>`;
    }

    private renderContextNavigation(): Nullable<TemplateResult> {
        if (this.level <= 2) {
            return null;
        }

        return html`
            ${this.renderBackNavigation()}
            ${this.renderParentLink()}
        `;
    }

    private renderParentLink(): Nullable<TemplateResult> {
        if (!this.navItem) {
            return null;
        }

        if (this.navItem.hasLink()) {
            return html`
                <div class="parent-navigation" data-eventelement="parent-navigation">
                    <a href=${this.navItem.href}
                       target=${this.navItem.target}
                       title=${this.navItem.label}
                       class="parent-item parent-link">
                        ${this.navItem.label}
                    </a>
                </div>`;
        } else {
            return html`
                <div class="parent-navigation">
                    <div title=${this.navItem.label} class="parent-item">${this.navItem.label}</div>
                </div>`;
        }
    }

    private renderBackNavigation(): TemplateResult {
        const backNavigationLabel = this.dictionary.translate("MSG_BACK_NAVIGATION_LABEL");
        return html`
            <button class="back-navigation" data-eventelement="back-navigation" @click=${this.navigateBack}>${backNavigationLabel}</button>`;
    }

    private navigateBack(): void {
        this.dispatchEvent(navigationUpEvent());
        this.trackNavigationBack();
    }

    private trackNavigationBack(): void {
        this.tracking.handleNavigationInteract({
            name: TrackingEventName.NAVIGATION_INTERACT,
            data: {
                text: this.navItem?.label ?? this.dictionary.translate("MSG_BACK_NAVIGATION_LABEL"),
                contentId: "header.back-navigation",
                location: TrackingEventLocation.HEADER,
                type: TrackingEventType.CLOSE
            }
        });
    }
}